import { Injectable, inject } from '@angular/core';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';

import { TelegramErrorReporterService } from 'src/app/core/services/telegram-error-reporter.service';

/**
 * `ToastrService` with error toasts mirrored to the Telegram alert channel.
 *
 * Substituted for `ToastrService` in the root injector (app.module), so all 350+ existing
 * `this.toastr.error(...)` call sites are covered without being touched — and any new one
 * is covered automatically.
 *
 * Only `error()` is mirrored. `warning()` is mostly guidance ("please wait for uploads"),
 * and success/info never indicate a fault.
 *
 * The toast is what the user actually saw, which is why it is worth reporting even though
 * most error toasts follow an API failure the interceptor already reported: the interceptor
 * knows the request failed, this knows whether the user was told, and in what words. The
 * reporter's echo window collapses the pair when both carry the same text.
 */
@Injectable()
export class TrackedToastrService extends ToastrService {
  private readonly reporter = inject(TelegramErrorReporterService);

  override error<ConfigPayload = any>(
    message?: string,
    title?: string,
    override?: Partial<IndividualConfig<ConfigPayload>>
  ): ActiveToast<any> {
    this.reportToast(message, title);
    return super.error(message, title, override);
  }

  private reportToast(message?: string, title?: string): void {
    const text = String(message ?? '').trim();
    if (!text) {
      return;
    }

    this.reporter.report({
      type: 'USER_MESSAGE',
      message: text,
      name: title ? String(title).trim() : undefined
    });
  }
}
